import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Button, Dialog, Spin } from '@fx-ui/fine-design';
import {
    compact,
    ECode,
    getSwitchCorpRedirectUri,
    groupBy,
    insertURLQuery,
    isEmpty,
    isNumber,
    map,
    redirect,
    URL_PARAMETER,
    UserTenant
} from '@libs/shared';
import { getUserTenants, switchCorp } from '../../../../apis';
import { ArrowRight } from '../../../../icons';
import { useAuthMfa } from '../../../auth-mfa';
import CorpJoinCreateDialog from '../../../corp-join-create-dialog';
import { platformIntl, SITE_ROUTER } from '../../../../constants';
import {
    getSiteUrl,
    hasFunctionRegisterPersona,
    hasFunctionTenantRegister
} from '../../../../utils';
import { authModel, globalState } from '../../../../models';
import siteRouterManager, { SiteRouterKey } from '../../../../utils/site-router';

interface ISwitchCorpProps {
    visible: boolean;
    onCancel: () => void;
    onCreateCorp?: () => void;
    /**
     * 切换、加入、创建企业成功后，页面跳转前的阻塞型回调
     * 返回 redirectUri 时覆盖默认跳转地址，reject 时中断跳转
     */
    onBeforeCorpAction?: (
        type: 'switch' | 'join' | 'create'
    ) => void | Promise<{ redirectUri?: string }>;
}

/**
 * 切换企业弹窗
 */
const SwitchCorpDialog: React.FC<ISwitchCorpProps> = (props) => {
    const { visible, onCancel, onCreateCorp, onBeforeCorpAction } = props;

    const [loading, setLoading] = useState(true);
    const [userCorps, setUserCorps] = useState<UserTenant[]>();
    const [createCorpVisible, setCreateCorpVisible] = useState(false);
    const [joinCorpVisible, setJoinCorpVisible] = useState(false);

    const { handleMfaError, renderAuthMfa } = useAuthMfa();

    const { isSyncMember, tenantId, isCurrentUserNoTenant } = authModel;

    useEffect(() => {
        if (visible) {
            getUserTenants().then((res) => {
                setUserCorps(res);
                setLoading(false);
            });
        }
    }, [visible]);

    const handleSwitchCorp = (corp: UserTenant) => {
        switchCorp(corp.tenant_id)
            .then((res) => {
                Promise.resolve(onBeforeCorpAction?.('switch'))
                    .then((result) => {
                        redirect(
                            insertURLQuery(getSiteUrl(SITE_ROUTER.ACCOUNT.SWITCH_CORP), [
                                {
                                    key: URL_PARAMETER.REDIRECT_URI,
                                    value: getSwitchCorpRedirectUri(result?.redirectUri)
                                },
                                {
                                    key: URL_PARAMETER.TOKEN,
                                    value: res.data.token
                                },
                                {
                                    key: URL_PARAMETER.TARGET,
                                    value: corp.tenant_id
                                }
                            ])
                        );
                    })
                    .catch(() => undefined);
            })
            .catch((error) => {
                if (
                    [ECode.USER_MFA_REQUIRED, ECode.USER_PHONE_UNBIND_BEFORE_MFA].includes(
                        error.response.data.code
                    )
                ) {
                    handleMfaError(error, () => {
                        handleSwitchCorp(corp);
                    });
                }
            });
    };

    const createCorpItem = (corp: UserTenant, isOwner: boolean) => {
        return (
            <div key={corp.tenant_id} className={classNames('corp-item', { 'is-owner': isOwner })}>
                <div className="corp-name" title={corp.name}>
                    {corp.name}
                </div>
                <div className="corp-op">
                    {tenantId === corp.tenant_id ? (
                        <span className="op-current">{platformIntl.get('corp.current')}</span>
                    ) : (
                        <Button
                            type="text"
                            onClick={() =>
                                corp.isNotMigrated
                                    ? globalState.onSwitchCorp?.(corp.tenant_id)
                                    : handleSwitchCorp(corp)
                            }
                        >
                            <div className="enter-corp-btn">
                                {platformIntl.get('corp.enter')}
                                <ArrowRight />
                            </div>
                        </Button>
                    )}
                </div>
                {renderAuthMfa()}
            </div>
        );
    };

    const renderContent = () => {
        // 非同步用户，且没有自己企业的，才可以创建企业
        const canCreateCorp = !isSyncMember && isCurrentUserNoTenant && hasFunctionTenantRegister();

        const { ownCorps = [], joinedCorps = [] } = groupBy(userCorps, (corp) => {
            return corp.isCorpOwner ? 'ownCorps' : 'joinedCorps';
        });

        return (
            <>
                <div className="corp-block">
                    {(!isEmpty(ownCorps) || canCreateCorp) && (
                        <div className="corp-desc">{platformIntl.get('corp.my.create.team')}</div>
                    )}
                    {!isEmpty(ownCorps) && (
                        <div className="corp-wrapper">
                            {map(ownCorps, (corp) => createCorpItem(corp, true))}
                        </div>
                    )}
                    {canCreateCorp && (
                        <Button
                            className="corp-switch-btn"
                            type="normal"
                            onClick={() => {
                                if (onCreateCorp) {
                                    onCreateCorp();
                                } else if (hasFunctionRegisterPersona()) {
                                    redirect(
                                        insertURLQuery(
                                            siteRouterManager.getPath(SiteRouterKey.Persona),
                                            compact([
                                                isNumber(globalState.suiteId) && {
                                                    key: URL_PARAMETER.SUITE_ID,
                                                    value: globalState.suiteId
                                                }
                                            ])
                                        )
                                    );
                                } else {
                                    setCreateCorpVisible(true);
                                }
                            }}
                        >
                            {platformIntl.get('corp.create')}
                        </Button>
                    )}
                </div>
                <div className="corp-block">
                    <div className="corp-desc">{platformIntl.get('corp.my.join.team')}</div>
                    {joinedCorps.length > 0 && (
                        <div className="corp-wrapper">
                            {map(joinedCorps, (corp) => createCorpItem(corp, false))}
                        </div>
                    )}
                    <Button
                        className="corp-switch-btn"
                        type="normal"
                        onClick={() => setJoinCorpVisible(true)}
                    >
                        {platformIntl.get('corp.join')}
                    </Button>
                </div>
            </>
        );
    };

    return (
        <Dialog
            visible={visible}
            title={platformIntl.get('switchCorp')}
            text4Ok={null}
            text4Cancel={null}
            height={470}
            onCancel={onCancel}
            classNames={{
                wrapper: 'fx-switch-corp-dialog',
                body: 'dialog-content'
            }}
        >
            {loading ? <Spin /> : renderContent()}
            <CorpJoinCreateDialog
                type="join"
                visible={joinCorpVisible}
                setVisible={setJoinCorpVisible}
                onBeforeCorpAction={onBeforeCorpAction}
            />
            <CorpJoinCreateDialog
                type="create"
                visible={createCorpVisible}
                setVisible={setCreateCorpVisible}
                onBeforeCorpAction={onBeforeCorpAction}
            />
        </Dialog>
    );
};

export default SwitchCorpDialog;
